import React, { useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGlowAnimation } from '@/hooks/animations/useGlowAnimation';
import { Objective } from '@/types/objective';
import '@/styles/animations/glow.css';

interface ObjectiveSelectorProps {
  objectives: Objective[];
  selectedObjective: string;
  onSelect: (objective: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  needsAttention?: boolean;
}

export default function ObjectiveSelector({
  objectives,
  selectedObjective,
  onSelect,
  isOpen,
  onToggle,
  needsAttention = false
}: ObjectiveSelectorProps) {
  const { isGlowing, startGlow } = useGlowAnimation();

  useEffect(() => {
    if (needsAttention) {
      startGlow();
    }
  }, [needsAttention, startGlow]);

  const renderObjectiveText = (objective: string) => {
    return <span dangerouslySetInnerHTML={{ __html: objective }} />;
  };

  return (
    <div className="relative">
      <div 
        className={`absolute inset-0 rounded-lg overflow-hidden ${
          isGlowing ? 'highlight-attention' : ''
        }`}
      >
        <div 
          className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto]"
          style={{ 
            animation: isGlowing ? 'none' : 'gradientAnimation 8s linear infinite'
          }}
        />
      </div>
      
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
          >
            {objectives.map((objective) => (
              <button
                key={objective.id}
                onClick={() => {
                  onSelect(objective.name);
                  onToggle();
                }}
                className="w-full px-4 py-4 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b last:border-b-0 border-gray-100 group transition-all duration-200"
              >
                <div className="font-medium text-gray-900 mb-1 group-hover:text-primary transition-colors">
                  {renderObjectiveText(objective.name)}
                </div>
                <p className="text-sm text-gray-600">{objective.description}</p>
                {objective.addedCost && (
                  <p className="text-sm text-green-600 mt-1">
                    <s>+${objective.addedCost}</s> No added cost for Advanced plans
                  </p>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}